import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import LoginPage from "./pages/LoginPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import DashboardLayout from "./components/layout/DashboardLayout";
import DashboardPage from "./pages/DashboardPage";
import PipelinePage from "./pages/PipelinePage";
import CustomersPage from "./pages/CustomersPage";
import CustomerDetailPage from "./pages/CustomerDetailPage";
import ProvisionsPage from "./pages/ProvisionsPage";
import ChecklistMasterPage from "./pages/ChecklistMasterPage";
import IncidentsPage from "./pages/IncidentsPage";
import RequestsPage from "./pages/RequestsPage";
import ITTicketsPage from "./pages/ITTicketsPage";
import CatalogPage from "./pages/CatalogPage";
import ProvidersPage from "./pages/ProvidersPage";
import SettingsPage from "./pages/SettingsPage";
import AuditLogPage from "./pages/AuditLogPage";
import ProfilePage from "./pages/ProfilePage";
import UsersPage from "./pages/UsersPage";
import AnnouncementsPage from "./pages/AnnouncementsPage";
import ReportsPage from "./pages/ReportsPage";
import CoverageSearchPage from "./pages/CoverageSearchPage";
import "./App.css";

const ProtectedRoute = ({ children, requiredPermission }) => {
  const { user, loading, canView } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#04042C] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#42E695]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // `requiredPermission` gates the route on the same permission matrix the
  // sidebar uses via `canView(module)`. This keeps sidebar visibility and
  // direct-URL access in lock-step:
  //   * a user who sees the entry in the sidebar can open the route, and
  //   * a user who lacks the permission cannot reach the route by typing
  //     the URL manually either.
  if (requiredPermission && !canView(requiredPermission)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardLayout>
                <DashboardPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/pipeline" element={
            <ProtectedRoute>
              <DashboardLayout>
                <PipelinePage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/customers" element={
            <ProtectedRoute>
              <DashboardLayout>
                <CustomersPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/customers/:id" element={
            <ProtectedRoute>
              <DashboardLayout>
                <CustomerDetailPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/provisions" element={
            <ProtectedRoute>
              <DashboardLayout>
                <ProvisionsPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/checklist-master" element={
            <ProtectedRoute requiredPermission="checklist_master">
              <DashboardLayout>
                <ChecklistMasterPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/incidents" element={
            <ProtectedRoute>
              <DashboardLayout>
                <IncidentsPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/requests" element={
            <ProtectedRoute>
              <DashboardLayout>
                <RequestsPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/it-support" element={
            <ProtectedRoute>
              <DashboardLayout>
                <ITTicketsPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />

          <Route path="/coverage-search" element={
            <ProtectedRoute>
              <DashboardLayout>
                <CoverageSearchPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/catalog" element={
            <ProtectedRoute requiredPermission="catalog">
              <DashboardLayout>
                <CatalogPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/providers" element={
            <ProtectedRoute requiredPermission="providers">
              <DashboardLayout>
                <ProvidersPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/settings" element={
            <ProtectedRoute requiredPermission="settings">
              <DashboardLayout>
                <SettingsPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/audit-log" element={
            <ProtectedRoute requiredPermission="audit">
              <DashboardLayout>
                <AuditLogPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/users" element={
            <ProtectedRoute requiredPermission="users">
              <DashboardLayout>
                <UsersPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/announcements" element={
            <ProtectedRoute requiredPermission="announcements">
              <DashboardLayout>
                <AnnouncementsPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          {/* /reports is gated by `requiredPermission="reports"` so sidebar +
              direct-URL access stay in lock-step like the rest of the app. */}
          <Route path="/reports" element={
            <ProtectedRoute requiredPermission="reports">
              <DashboardLayout>
                <ReportsPage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/profile" element={
            <ProtectedRoute>
              <DashboardLayout>
                <ProfilePage />
              </DashboardLayout>
            </ProtectedRoute>
          } />
        </Routes>
        <Toaster richColors position="top-right" />
        </BrowserRouter>
      </SettingsProvider>
    </AuthProvider>
  );
}

export default App;
